import {
  ApiError,
  type BillingMachineResource,
  BillingMachinesController_findOne_v1,
  type CatalogueItemResource,
  CatalogueItemsController_list_v1,
  type ProductCodeMappingResource,
  ProductCodesController_list_v1,
  type UserResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import { Switch } from '@repo/ui/components/switch';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconArrowLeft, IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isNotFound } from '@/api/failure';
import { formatInstant } from '@/lib/instant';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { useActorNames } from '../catalogue/actor-names';
import { billingMachinePath } from '../paths';
import { type MappingDraft, MapProductCodeDialog } from './MapProductCodeDialog';

/**
 * One billing machine's product codes, and the catalogue item each means (#229).
 *
 * A bill in the machine's export names a product by the code the machine prints.
 * Without a mapping for that code on this machine, every bill using it is
 * refused as `MACHINE_PRODUCT_CODE_UNMAPPED`; with one whose printed name no
 * longer matches, as `MACHINE_PRODUCT_CODE_REPROGRAMMED`. This screen is where
 * both are fixed, by owners and managers alike.
 *
 * `?code=&name=` opens the mapping form prefilled — the link an upload's
 * `UNMAPPED` refusal carries (`billingMachineProductCodesPath`).
 *
 * Read whole: the codes are bounded by the machine's keypad, and even with the
 * replaced ones shown the history grows only when somebody re-maps a code.
 */

type Loaded<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: T }
  | { readonly kind: 'failed'; readonly error: unknown };

const COLUMNS = 5;

const backLink =
  'inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30';

export function ProductCodes() {
  const client = useApiClient();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [machine, setMachine] = useState<Loaded<BillingMachineResource>>({ kind: 'loading' });
  const [mappings, setMappings] = useState<Loaded<readonly ProductCodeMappingResource[]>>({
    kind: 'loading',
  });
  const [items, setItems] = useState<Loaded<readonly CatalogueItemResource[]>>({
    kind: 'loading',
  });
  const [includeReplaced, setIncludeReplaced] = useState(false);
  const [justMapped, setJustMapped] = useState<ProductCodeMappingResource | null>(null);

  // Opened from the address once, when an upload's refusal linked here.
  const [draft, setDraft] = useState<MappingDraft | null>(() => {
    const code = searchParams.get('code');
    return code === null ? null : { code, name: searchParams.get('name') ?? '' };
  });

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  const actors = useActorNames();

  useEffect(() => {
    if (id === undefined) return;

    const controller = new AbortController();
    let live = true;

    setMachine({ kind: 'loading' });

    client
      .request(BillingMachinesController_findOne_v1, { path: { id }, signal: controller.signal })
      .then((value) => {
        if (live) setMachine({ kind: 'loaded', value });
      })
      .catch((error: unknown) => {
        if (live) setMachine({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, id, reloads]);

  useEffect(() => {
    if (id === undefined) return;

    const controller = new AbortController();
    let live = true;

    setMappings({ kind: 'loading' });

    collectPages<ProductCodeMappingResource>((offset, limit) =>
      client.request(ProductCodesController_list_v1, {
        path: { id },
        query: { offset, limit, includeReplaced },
        signal: controller.signal,
      }),
    )
      .then((value) => {
        if (live) setMappings({ kind: 'loaded', value });
      })
      .catch((error: unknown) => {
        if (live) setMappings({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, id, includeReplaced, reloads]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Retired items excluded — the API's default — because mapping to one is
    // refused `MACHINE_PRODUCT_ITEM_RETIRED`. The picker never offers them.
    collectPages<CatalogueItemResource>((offset, limit) =>
      client.request(CatalogueItemsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((value) => {
        if (live) setItems({ kind: 'loaded', value });
      })
      .catch((error: unknown) => {
        if (live) setItems({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  if (id === undefined) return null;

  const loadedMachine = machine.kind === 'loaded' ? machine.value : null;

  const missing =
    machine.kind === 'failed' &&
    (isNotFound(machine.error) ||
      (machine.error instanceof ApiError && machine.error.code === 'VALIDATION_FAILED'));

  if (missing) return <Navigate to={billingMachinePath(id)} replace />;

  const failure =
    mappings.kind === 'failed' ? mappings : machine.kind === 'failed' ? machine : null;

  const closeDraft = () => {
    setDraft(null);
    // Drop the prefill from the address, so a reload does not reopen the form.
    if (searchParams.has('code')) setSearchParams({}, { replace: true });
  };

  return (
    <Screen
      title="Product codes"
      lede={loadedMachine === null ? undefined : loadedMachine.name}
      description="The code a billing machine prints for each product, and the catalogue item it means. A bill whose code is not mapped here is refused on upload, and so is one whose printed name no longer matches the name recorded here."
      action={
        <Button onClick={() => setDraft({ code: '', name: '' })}>
          <IconPlus />
          Map a code
        </Button>
      }
      aside={
        <>
          {loadedMachine !== null && (
            <ScreenFact label="Machine ID">
              <span className="font-mono numeric">{loadedMachine.machineCode}</span>
            </ScreenFact>
          )}

          <div className="flex items-center gap-3">
            <Switch
              id="show-replaced"
              aria-labelledby="show-replaced-label"
              checked={includeReplaced}
              onCheckedChange={setIncludeReplaced}
            />
            <Label
              id="show-replaced-label"
              htmlFor="show-replaced"
              className="cursor-pointer text-sm"
            >
              Show replaced mappings
            </Label>
          </div>

          <Link to={billingMachinePath(id)} className={backLink}>
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the machine
          </Link>
        </>
      }
    >
      {justMapped !== null && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>
            <span className="font-mono text-foreground numeric">{justMapped.productCode}</span> now
            means {justMapped.catalogueItem.name}. Upload the export again to import the bills that
            were refused for it — bills already imported are not counted twice.
          </AlertDescription>
        </Alert>
      )}

      <ScreenTable label="Product codes">
        <TableHeader>
          <TableRow>
            <TableHead className="w-28">Code</TableHead>
            <TableHead>Printed name</TableHead>
            <TableHead>Catalogue item</TableHead>
            <TableHead className="w-72">Recorded</TableHead>
            <TableHead className="w-28 text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>

        {failure === null && mappings.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

        {failure !== null && (
          <TableNotice columns={COLUMNS}>
            <LoadFailed error={failure.error} onRetry={reload} />
          </TableNotice>
        )}

        {failure === null &&
          mappings.kind === 'loaded' &&
          (mappings.value.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No product codes mapped yet"
                description="Every bill from this machine is refused until its product codes are mapped. Map a code takes the code and the name exactly as the machine prints them, and the catalogue item they mean."
              />
            </TableNotice>
          ) : (
            <TableBody>
              {mappings.value.map((mapping, index) => {
                // Newest first within each code, so a row is in force when it
                // is the first of its code on the list.
                const replaced =
                  mappings.value.findIndex((m) => m.productCode === mapping.productCode) !== index;

                return (
                  <MappingRow
                    key={`${mapping.productCode}-${mapping.recordedAt}`}
                    mapping={mapping}
                    replaced={replaced}
                    actor={actors.get(mapping.recordedByActorId)}
                    onRemap={() =>
                      setDraft({
                        code: mapping.productCode,
                        name: mapping.machineProductName,
                        remapping: true,
                      })
                    }
                  />
                );
              })}
            </TableBody>
          ))}
      </ScreenTable>

      <MapProductCodeDialog
        machineId={id}
        draft={draft}
        items={items.kind === 'loaded' ? items.value : null}
        itemsFailed={items.kind === 'failed'}
        onClose={closeDraft}
        onMapped={(mapping) => {
          closeDraft();
          setJustMapped(mapping);
          reload();
        }}
      />
    </Screen>
  );
}

function MappingRow({
  mapping,
  replaced,
  actor,
  onRemap,
}: {
  mapping: ProductCodeMappingResource;
  replaced: boolean;
  actor: UserResource | undefined;
  onRemap: () => void;
}) {
  const item = mapping.catalogueItem;

  return (
    <TableRow className={replaced ? 'text-muted-foreground' : undefined}>
      <TableCell className={`font-mono numeric ${replaced ? '' : 'text-foreground'}`}>
        {mapping.productCode}
        {replaced && (
          <>
            {' '}
            <Badge variant="outline">Replaced</Badge>
          </>
        )}
      </TableCell>
      {/* Literal text from the machine's own program — rendered as text. */}
      <TableCell className="font-mono whitespace-normal">{mapping.machineProductName}</TableCell>
      <TableCell className="whitespace-normal">
        <span className={replaced ? undefined : 'text-foreground'}>{item.name}</span>{' '}
        <span className="font-mono text-muted-foreground numeric">{item.code}</span>
        {item.retired && (
          <>
            {' '}
            <Badge variant="outline">Retired</Badge>
          </>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        <span className="numeric">{formatInstant(mapping.recordedAt)}</span>
        {' · '}
        {actor === undefined ? (
          <span className="font-mono text-xs numeric">{mapping.recordedByActorId}</span>
        ) : (
          <span className="text-foreground">{actor.displayName}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {!replaced && (
          <Button
            variant="outline"
            size="sm"
            aria-label={`Re-map ${mapping.productCode}`}
            onClick={onRemap}
          >
            Re-map
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
